import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Intro = ({ onComplete }: { onComplete: () => void }) => {
  const [showLogo, setShowLogo] = useState(false);
  const [showText, setShowText] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // Stage 1: Fade in logo after a small delay
    const logoTimer = setTimeout(() => setShowLogo(true), 500);
    
    // Stage 2: Fade in text after logo starts showing
    const textTimer = setTimeout(() => setShowText(true), 1500);
    
    // Stage 3: Keep visible for 3 seconds then fade out everything
    const fadeOutTimer = setTimeout(() => setFadeOut(true), 4500);
    
    // Final Stage: Notify completion to unmount intro
    const completeTimer = setTimeout(() => onComplete(), 5500);

    return () => {
      clearTimeout(logoTimer);
      clearTimeout(textTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: fadeOut ? 0 : 1 }}
      transition={{ duration: 1, ease: "easeInOut" }}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background pointer-events-none overflow-hidden"
    >
      <div className="relative flex flex-col items-center gap-6">
        <AnimatePresence>
          {showLogo && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="relative"
            >
              <img 
                src="/logo.png" 
                alt="Melody Art Logo" 
                className="w-32 h-32 md:w-48 md:h-48 object-contain filter drop-shadow-[0_0_15px_rgba(255,215,0,0.3)]"
              />
              {/* Optional glow ring */}
              <motion.div 
                className="absolute inset-0 rounded-full border border-primary/20"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1.5, opacity: 0 }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeOut" }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showText && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="text-center space-y-2"
            >
              <h1 className="font-display text-3xl md:text-5xl font-bold text-gradient tracking-wider">
                Melody Art Gallery
              </h1>
              <p className="font-sans text-sm md:text-lg text-muted-foreground uppercase tracking-[0.3em] font-light">
                Premium Artistic Expression
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default Intro;
